import { Button, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import {
  ApiError,
  type Branding,
  type InvitationDetail,
  acceptInvitation,
  branding,
  lookupInvitation,
  signIn,
} from "../../data/api";
import { bareMessage } from "../../data/data";
import { Failure, Field, Mono } from "../../ui/components/primitives";
import { LandingLayout } from "./LandingLayout";

// ---
// Invitation acceptance
//
// Shown before sign-in, because the invitee has no session yet. The token comes from the URL and
// is never stored: it is single-use, so persisting it would only widen the window it could leak in.
//
// Two things this page deliberately does not make somebody wait for.
//
// The fields are drawn before the lookup answers. Only the subtitle needs to know whose
// invitation this is; the inputs do not, and a heading over an empty space while a request is in
// flight is a page that has not loaded yet as far as the reader is concerned.
//
// And accepting signs them in. At that moment the browser is holding a password we have just
// stored, for an account whose single-use token they have just redeemed — which is exactly what
// signing in with that password would prove a moment later. Making them type it again, on a
// screen reached by clicking through a confirmation, is three steps to do one thing.
// ---

const MIN_LENGTH = 12;

export function Accept({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}): React.JSX.Element {
  const [detail, setDetail] = useState<InvitationDetail | null>(null);
  // Fetched after the lookup, because only the invitation says which tenant this is for. Best
  // effort: a page that cannot load a logo is still a page somebody can set a password on.
  const [brand, setBrand] = useState<Branding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void lookupInvitation(token)
      .then((found) => {
        setDetail(found);
        void branding
          .read(found.tenant)
          .then(setBrand)
          .catch(() => undefined);
      })
      .catch((caught: unknown) => setError(bareMessage(caught)));
  }, [token]);

  if (done) {
    const onward = detail?.continueTo ?? null;
    return (
      <LandingLayout
        brand={brand}
        title="Account ready"
        subtitle="Your password is set. Sign in to continue — this is only shown when we could not do it for you."
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => {
            if (onward === null) onDone();
            else window.location.assign(onward);
          }}
        >
          {onward === null ? "Go to sign in" : "Continue"}
        </Button>
      </LandingLayout>
    );
  }

  if (error !== null && detail === null) {
    // The server does not say which of expired, used, or unknown applies, and neither should this.
    return (
      <LandingLayout
        brand={brand}
        title="Invitation unavailable"
        subtitle="It may have expired, already been used, or never existed. Ask an administrator to send a new one."
      >
        <Button variant="outlined" size="large" fullWidth onClick={onDone}>
          Go to sign in
        </Button>
        <Failure error={error} />
      </LandingLayout>
    );
  }

  const mismatch = confirm !== "" && confirm !== password;
  const tooShort = password !== "" && password.length < MIN_LENGTH;

  return (
    <LandingLayout
      brand={brand}
      title="Set your password"
      subtitle={
        detail === null ? (
          "Checking the invitation…"
        ) : (
          <>
            <Mono>{detail.email}</Mono> joining <Mono>{detail.tenant}</Mono>. The invitation expires{" "}
            {new Date(detail.expiresAt).toLocaleString()}.
          </>
        )
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void acceptInvitation(token, password)
            .then(async (accepted) => {
              // A full load rather than a client-side navigation: the console reads the
              // session once, at boot, and it has already decided there is none.
              try {
                await signIn(accepted.email, password);
                window.location.assign(detail?.continueTo ?? "/");
              } catch {
                // The account is set up either way — the invitation is spent and the password
                // is stored. Only the convenience failed, so say what is true and offer the
                // way in rather than reporting an error about work that succeeded.
                setDone(true);
              }
            })
            .catch((caught: unknown) => setError(bareMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        <Stack spacing={4}>
          <Field
            label="New password"
            error={tooShort}
            hint={`At least ${MIN_LENGTH} characters. This link works once.`}
          >
            <TextField
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
              autoFocus
              autoComplete="new-password"
              error={tooShort}
            />
          </Field>
          <Field label="Confirm" error={mismatch} hint={mismatch ? "Passwords do not match" : " "}>
            <TextField
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={mismatch}
            />
          </Field>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={
              detail === null || busy || mismatch || tooShort || password === "" || confirm === ""
            }
          >
            {busy ? "Setting…" : "Set password"}
          </Button>
        </Stack>
      </form>
      <Failure error={error} />
    </LandingLayout>
  );
}
